import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { FormikHelpers } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MutationCreateProductArgs,
  MutationDeleteProductArgs,
  MutationUpdateProductArgs,
  Product as GqlProduct,
  ProductReviewStatus,
  ProductStatus,
  ProductVariant,
  SellerSettings,
} from '@shopify-multiseller/graphql/partner/types';
import { useNotification } from '../../components';
import { useAuth, useConfig, useSettings } from '../../hooks';
import {
  CREATE_PRODUCT_MUTATION,
  DELETE_PRODUCT_MUTATION,
  GET_PRODUCT_QUERY,
  UPDATE_PRODUCT_MUTATION,
} from './queries';
import { IProductInitialValuesProps } from './types';
import { buildEmptyProductInput, formValuesToProductInput, productToFormValues } from './mappers';

/**
 * State + data controller for the product editor.
 *
 * Owns loading of the product, the add/edit branching, derivation of the Formik
 * initial values (via `mappers`), and the create/update submit. The presentation
 * lives in `ProductFormBody`; this hook is the single home for the editor's
 * imperative logic.
 */
export interface ProductFormController {
  product: GqlProduct | undefined;
  variants: ProductVariant[] | undefined;
  error: Error | undefined;
  loadingError: Error | undefined;
  productInput: IProductInitialValuesProps | undefined;
  loading: boolean;
  isAddProduct: boolean;
  trackQuantity: boolean;
  setTrackQuantity: React.Dispatch<React.SetStateAction<boolean>>;
  showContentReviewModal: boolean;
  setShowContentReviewModal: React.Dispatch<React.SetStateAction<boolean>>;
  showFullReviewModal: boolean;
  setShowFullReviewModal: React.Dispatch<React.SetStateAction<boolean>>;
  showSubmitErrorModal: boolean;
  setShowSubmitErrorModal: React.Dispatch<React.SetStateAction<boolean>>;
  showArchiveModal: boolean;
  setShowArchiveModal: React.Dispatch<React.SetStateAction<boolean>>;
  archiving: boolean;
  handleArchive: () => void;
  disableProductUntilChangesAccepted: boolean;
  setDisableProductUntilChangesAccepted: React.Dispatch<React.SetStateAction<boolean>>;
  settings: SellerSettings;
  disabled: boolean;
  handleSubmit: (
    _values: IProductInitialValuesProps,
    _actions: FormikHelpers<IProductInitialValuesProps>,
  ) => void;
}

export const useProductForm = (): ProductFormController => {
  const params = useParams();
  const [product, setProduct] = useState<GqlProduct>();
  const [variants, setVariants] = useState<ProductVariant[] | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loadingError, setLoadingError] = useState<Error | undefined>(undefined);
  const [productInput, setProductInput] = useState<IProductInitialValuesProps | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [trackQuantity, setTrackQuantity] = useState<boolean>(false);
  const [showContentReviewModal, setShowContentReviewModal] = useState<boolean>(false);
  const [showFullReviewModal, setShowFullReviewModal] = useState<boolean>(false);
  const [showSubmitErrorModal, setShowSubmitErrorModal] = useState<boolean>(false);
  const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
  const [archiving, setArchiving] = useState<boolean>(false);
  const [disableProductUntilChangesAccepted, setDisableProductUntilChangesAccepted] =
    useState<boolean>(false);
  const [isAddProduct, setIsAddProduct] = useState<boolean>(false);

  const notification = useNotification();
  const navigate = useNavigate();
  const settings = useSettings();
  const config = useConfig();
  const auth = useAuth();

  const [getProduct] = useLazyQuery<{ product: GqlProduct }, { id: string }>(GET_PRODUCT_QUERY, {
    // 'no-cache' instead of 'network-only': the product editor mutates a
    // local Formik copy and never re-reads from cache, so caching the
    // response just risks serving stale data after a save. Also avoids a
    // dev-mode crash where Apollo's InMemoryCache strict-mode strips
    // mock responses that are missing nested fragment fields.
    fetchPolicy: 'no-cache',
  });

  const [updateProduct] = useMutation<{ updateProduct: GqlProduct }, MutationUpdateProductArgs>(
    UPDATE_PRODUCT_MUTATION,
  );

  const [createProduct] = useMutation<{ createProduct: GqlProduct }, MutationCreateProductArgs>(
    CREATE_PRODUCT_MUTATION,
  );

  const [deleteProduct] = useMutation<{ deleteProduct: boolean }, MutationDeleteProductArgs>(
    DELETE_PRODUCT_MUTATION,
  );

  useEffect(() => {
    if (params.productId) {
      getProduct({
        variables: {
          id: params.productId,
        },
      })
        .then((result) => {
          if (result.error) {
            setLoadingError(new Error('Error fetching product'));
          } else {
            setProduct(result.data?.product);
          }
          setLoading(false);
        })
        .catch((err) => {
          setError(err);
          setLoading(false);
        });
    } else {
      setLoading(false);
      setIsAddProduct(true);
    }
  }, [params.productId]);

  useEffect(() => {
    if (isAddProduct) {
      const { input, trackQuantity: initialTrackQuantity } = buildEmptyProductInput(
        settings,
        config?.shopConfig?.shop.countryCode,
        auth?.user?.seller?.address?.countryCode,
      );
      setTrackQuantity(initialTrackQuantity);
      setProductInput(input);
    }
  }, [isAddProduct, settings.productType?.productType]);

  useEffect(() => {
    if (product) {
      const { input, variants: sourceVariants, trackQuantity: initialTrackQuantity } =
        productToFormValues(product, settings);
      setVariants(sourceVariants);
      setProductInput(input);
      if (initialTrackQuantity !== undefined) {
        setTrackQuantity(initialTrackQuantity);
      }
    }
  }, [product]);

  const handleSubmit = (
    values: IProductInitialValuesProps,
    actions: FormikHelpers<IProductInitialValuesProps>,
  ) => {
    if (!productInput) return;
    setUpdating(true);
    if (isAddProduct) {
      createProduct({
        variables: {
          product: formValuesToProductInput(values, productInput.id),
        },
      })
        .then((res) => {
          if (res.errors) {
            setShowSubmitErrorModal(true);
          } else {
            setProduct(res.data?.createProduct);
            notification.success({
              message: 'Your product has been created.',
            });
            // wait for 1 second before navigating
            setTimeout(() => {
              navigate('/products/' + res.data?.createProduct.id, { state: { createProduct: true } });
            }, 1000);
          }
        })
        .catch(() => {
          setShowSubmitErrorModal(true);
          actions.setSubmitting(false);
        });
    } else {
      updateProduct({
        variables: {
          product: formValuesToProductInput(values, productInput.id),
          disableProductUntilChangesApproved: disableProductUntilChangesAccepted,
        },
      })
        .then((res) => {
          if (res.errors) {
            setShowSubmitErrorModal(true);
            setUpdating(false);
          } else {
            setProduct(res.data?.updateProduct);
            notification.success({
              message: 'Your product has been updated.',
            });
            actions.setSubmitting(false);
            setUpdating(false);
          }
        })
        .catch(() => {
          setShowSubmitErrorModal(true);
          actions.setSubmitting(false);
          setUpdating(false);
        });
    }
  };

  /**
   * Take the product out of the store.
   *
   * The design puts this beside the title, where the seller is already looking
   * at the product they want gone. It was reachable only through the API
   * before, so a seller who had listed something by mistake had to ask the shop
   * to remove it.
   */
  const handleArchive = () => {
    if (!product) return;
    setArchiving(true);
    deleteProduct({ variables: { id: product.id } })
      .then((res) => {
        if (res.errors) {
          setArchiving(false);
          setShowArchiveModal(false);
          setShowSubmitErrorModal(true);
        } else {
          notification.success({ message: 'This product has been removed from the store.' });
          navigate('/products');
        }
      })
      .catch(() => {
        setArchiving(false);
        setShowArchiveModal(false);
        setShowSubmitErrorModal(true);
      });
  };

  /**
   * Can the seller still edit this product?
   *
   * The rule was "anything not Approved is read-only", which locked a product
   * that is live and selling merely because an edit to it is queued — so the
   * seller could not fix a typo they had just spotted, or respond to anything,
   * until the shop got round to the previous change. The design draws that
   * exact state with an editable form, and it is safe: there is already a
   * pending version, and further edits update it rather than touching what is
   * live.
   *
   * Still locked where editing would be meaningless or misleading:
   * - Archived — the product is out of the store.
   * - Pending — a first submission with nothing live behind it; editing while
   *   the shop is looking at it would move the thing being reviewed.
   * - Rejected / ChangesRejected — the shop has asked for something, and the
   *   screens for that are the rejection flows, not the editor.
   */
  const editableWhileReviewed =
    product?.status === ProductStatus.Active &&
    product?.reviewStatus === ProductReviewStatus.ChangesPending;

  const disabled: boolean =
    (product &&
      ((product.reviewStatus !== ProductReviewStatus.Approved && !editableWhileReviewed) ||
        product.status === ProductStatus.Archived)) ||
    updating;

  return {
    product,
    variants,
    error,
    loadingError,
    productInput,
    loading,
    isAddProduct,
    trackQuantity,
    setTrackQuantity,
    showContentReviewModal,
    setShowContentReviewModal,
    showFullReviewModal,
    setShowFullReviewModal,
    showSubmitErrorModal,
    setShowSubmitErrorModal,
    showArchiveModal,
    setShowArchiveModal,
    archiving,
    handleArchive,
    disableProductUntilChangesAccepted,
    setDisableProductUntilChangesAccepted,
    settings,
    disabled,
    handleSubmit,
  };
};
